import orderBy from 'lodash/orderBy'
import type {ActionPlanOrder} from '~/models/actionPlanOrder'
import type {ActionPlan, ActionPlanType, FullContract} from '~/models/contract'
import type {Option, TimelineItem} from '~/models/util'
import type {AllmaAction} from '~/models/allmajob'
import {setDefaultDeliveryDates} from './deliveryDates'
import {ALLMA_JOB_TYPE_ADDITIVE_PLANTING, ALLMA_JOB_TYPE_YOUNG_TREE_CARE} from '~/constants/allmaJobs'
import {CONTRACT_TYPE_SILVICULTURE_AGREEMENT, CONTRACT_TYPE_SILVICULTURE_ORDER} from '~/constants/contracts'
import {
    ACTIONPLAN_PLANTING_TYPES,
    ACTIONPLAN_PLANTING_TYPES_NEITHER,
    ACTIONPLAN_PLANTING_TYPES_OPPORTUNITY,
    ACTIONPLAN_PLANTING_TYPES_ORDER,
    ACTIONPLAN_PREVENT_PLANTING_IDS,
    ACTIONPLAN_REALLOCATED_AREA_OPEN,
    ACTIONPLAN_REALLOCATED_AREA_OTHER,
    ACTIONPLAN_REALLOCATED_AREA_THINNING,
    ACTIONPLAN_SITE_QUALITIES,
    ACTIONPLAN_TYPES,
    ACTIONPLAN_TYPES_ADDITIVE_PLANTING,
    ACTIONPLAN_TYPES_DITCH_CLEARING,
    ACTIONPLAN_TYPES_FERTILIZATION,
    ACTIONPLAN_TYPES_PLANTATION_CONTROL,
    ACTIONPLAN_TYPES_PLANTING,
    ACTIONPLAN_TYPES_PLANTSALE,
    ACTIONPLAN_TYPES_PRECLEARING_FELLING,
    ACTIONPLAN_TYPES_PRECLEARING_THINNING,
    ACTIONPLAN_TYPES_SCARIFICATION,
    ACTIONPLAN_TYPES_SCARIFICATION_WITH_EXCAVATOR,
    ACTIONPLAN_TYPES_SCARIFICATION_WITH_SOWING,
    ACTIONPLAN_TYPES_SPRAYING,
    ACTIONPLAN_TYPES_STEMPRUNING,
    ACTIONPLAN_TYPES_YOUNG_TREE_CARE,
    getActionPlanStandardProduct
} from '~/constants/actionPlans'
import {usePlantingStandardService} from "~/services/plantingStandardService";
import {useReallocatedAreasService} from "~/services/reallocatedAreasService";

const { plantingStandard } = usePlantingStandardService()
const { reallocatedAreas } = useReallocatedAreasService()

export const setDefaultActionPlanData = (contract: FullContract): ActionPlan => {
  let actionPlan: ActionPlan = {
    AdditionalOrder: false,
    Area: 0,
    AreaPortion: 0,
    Meters: 0,
    PlantsPerAcre: 0,
    ProductId: getActionPlanStandardProduct.value,
    ReallocatedArea: contract.Type === CONTRACT_TYPE_SILVICULTURE_ORDER ? ACTIONPLAN_REALLOCATED_AREA_OTHER : ACTIONPLAN_REALLOCATED_AREA_OPEN,
    SupplementingUpTo: 0

  }

  ACTIONPLAN_TYPES.forEach((type) => {
    actionPlan[type.Field] = false
  })

  actionPlan.Planting = contract.Type === CONTRACT_TYPE_SILVICULTURE_ORDER ? ACTIONPLAN_PLANTING_TYPES_NEITHER : ACTIONPLAN_PLANTING_TYPES_ORDER

  actionPlan = setDefaultDeliveryDates(actionPlan, contract)

  return actionPlan
}

export const getPlantsPerAcreFromPlantingStandard = (actionPlan: ActionPlan): number => {
  const siteQuality = ACTIONPLAN_SITE_QUALITIES.find(s => s.Id === actionPlan.SiteQuality)
  if (siteQuality && actionPlan.Wood) {
    const standard = plantingStandard.value.find(p => p.SiteQuality === siteQuality.Name && p.WoodType === actionPlan.Wood)
    if (standard) {
      if (actionPlan.Scarification || actionPlan.ScarificationWithExcavator) {
        return standard.ScarifiedPlanting
      } else if (actionPlan.AdditivePlanting) {
        return standard.AdditivePlanting
      } else if (actionPlan.YoungTreecare) {
        return standard.YoungTreeCarePlanting
      } else {
        return standard.OrdinaryPlanting
      }
    }
  }

  return null
}

export function getPlantsPerAcreAllmaActionFromPlantingStandard (allmaAction: AllmaAction, jobType: string): number {
  const siteQuality = ACTIONPLAN_SITE_QUALITIES.find(s => s.Id === allmaAction.SiteQuality)
  if (siteQuality && allmaAction.Wood) {
    const standard = plantingStandard.value.find(p => p.SiteQuality === siteQuality.Name && p.WoodType === allmaAction.Wood)
    if (standard) {
      if (jobType === ALLMA_JOB_TYPE_ADDITIVE_PLANTING) {
        return standard.AdditivePlanting
      } else if (jobType === ALLMA_JOB_TYPE_YOUNG_TREE_CARE) {
        return standard.YoungTreeCarePlanting
      } else {
        return standard.OrdinaryPlanting
      }
    }
  }

  return null
}

export const getPlantingOptions = (additionalOrder: boolean): Option[] =>
  ACTIONPLAN_PLANTING_TYPES
    .filter(x => !additionalOrder || (additionalOrder && x.Id !== ACTIONPLAN_PLANTING_TYPES_OPPORTUNITY))
    .map((planting) => {
      const text = planting.Id === ACTIONPLAN_PLANTING_TYPES_ORDER ? 'Ja' : planting.Id === ACTIONPLAN_PLANTING_TYPES_OPPORTUNITY ? 'Salgsmulighet' : 'Nei'
      return {
        key: planting.Id,
        value: planting.Id,
        text
      }
    })

export const getSilvicultureActions = (actionPlan: ActionPlan): Option[] =>
  ACTIONPLAN_TYPES
    .filter(a => a.Id !== ACTIONPLAN_TYPES_PLANTING && !actionPlan[a.Field] && showSilvicultureAction(actionPlan, a))
    .map((action) => {
      return {
        key: action.Field,
        value: action.Id,
        text: action.Name,
        icon: action.Icon
      }
    })

const showSilvicultureAction = (actionPlan: ActionPlan, type: ActionPlanType): boolean => {
  for (const t of ACTIONPLAN_TYPES) {
    if (
      (type.Id === ACTIONPLAN_TYPES_PLANTATION_CONTROL && actionPlan.Planting === ACTIONPLAN_PLANTING_TYPES_NEITHER) ||
      (ACTIONPLAN_PREVENT_PLANTING_IDS.includes(type.Id) && actionPlan.Planting !== ACTIONPLAN_PLANTING_TYPES_NEITHER) ||
      ((actionPlan[t.Field] && type.excludeFromTypes && typeof type.excludeFromTypes.find(e => e === t.Id) !== 'undefined') || (type.excludeFromReallocatedAreas && typeof type.excludeFromReallocatedAreas.find(r => r === actionPlan.ReallocatedArea) !== 'undefined'))
    ) {
      return false
    }
  }
  return true
}

export const getActionPlanMinDate = (contract: FullContract, isAdditionalOrder: boolean): Date =>
  isAdditionalOrder ? new Date(new Date().getFullYear(), 1, 1) : new Date(contract.Year, 1, 1)

export const getActionPlanTimeline = (contract: FullContract, actionPlans: ActionPlan[]): TimelineItem[] => {
  const list = [] as TimelineItem[]

  const felling = {
    key: `hogger_${contract.LoggingMonth}_${contract.Year}`,
    title: 'FELLING',
    date: new Date(contract.Year, contract.LoggingMonth, 1),
    icon: 'spot/felling',
    subItems: []
  }

  const thinning = {
    key: `thinning_${contract.LoggingMonth}_${contract.Year}`,
    title: 'THINNING',
    date: new Date(contract.Year, contract.LoggingMonth, 1),
    icon: 'spot/thinning',
    subItems: []
  }

  actionPlans.forEach((actionPlan: ActionPlan) => {
    if (actionPlan.ReallocatedArea === ACTIONPLAN_REALLOCATED_AREA_THINNING) {
      thinning.subItems.push(`${actionPlan.Plot}/${actionPlan.Stand}`)
    } else if (contract.Type !== CONTRACT_TYPE_SILVICULTURE_ORDER && !actionPlan.AdditionalOrder) {
      felling.subItems.push(`${actionPlan.Plot}/${actionPlan.Stand}`)
    }

    ACTIONPLAN_TYPES.forEach((type: ActionPlanType) => {
      if (actionPlan[type.Field] && actionPlan[type.DateField]) {
        const key = `${type.Field}_${new Date(actionPlan[type.DateField]).getMonth()}_${new Date(actionPlan[type.DateField]).getFullYear()}`
        const existing = list.find(x => x.key === key) || null
        if (existing) {
          existing.subItems.push(`${actionPlan.Plot}/${actionPlan.Stand}`)
        } else {
          list.push({
            key,
            title: type.Name,
            date: new Date(actionPlan[type.DateField]),
            icon: type.Icon,
            subItems: [`${actionPlan.Plot}/${actionPlan.Stand}`]
          })
        }
      }
    })
  })

  if (felling.subItems.length > 0) {
    list.push(felling)
  }

  if (thinning.subItems.length > 0) {
    list.push(thinning)
  }

  return orderBy(list, 'date')
}

export const getOrdersFromActionPlans = (actionPlans: ActionPlan[], isAdditionalOrder: boolean): ActionPlanOrder[] => {
  const orders: ActionPlanOrder[] = []
  ACTIONPLAN_TYPES.forEach((type) => {
    const filteredPlans: ActionPlan[] = actionPlans.filter(a => a[type.Field] === type.checkValue && a.AdditionalOrder === isAdditionalOrder && !a.Deleted)
    if (filteredPlans.length > 0) {
      let volume: string | number = 0
      let volumeSecond: number = null
      switch (type.TypeOfVolume) {
        case 'AREA_DAA':
          volume = filteredPlans.reduce((prev, cur) => prev + cur.Area, 0).toFixed(2)
          break
        case 'NUMBER_OF_PLANTS_WITH_VOLUME':
          volume = filteredPlans.reduce((prev, cur) => prev + (cur.PlantsPerAcre ? cur.Area * cur.PlantsPerAcre : cur.Area), 0)
          break
        case 'NUMBER_OF_METERS_WITH_VOLUME':
          volume = filteredPlans.reduce((prev, cur) => prev + cur.Meters, 0)
          break
        case 'AREA_DAA_NUMBER_OF_PLANTS':
          volume = filteredPlans.reduce((prev, cur) => prev + cur.Area, 0).toFixed(2)
          volumeSecond = filteredPlans.reduce((prev, cur) => prev + (cur.PlantsPerAcre ? cur.Area * cur.PlantsPerAcre : cur.Area), 0)
          break
        default:
          break
      }
      orders.push({
        Name: type.Name,
        Text: type.TypeOfVolume,
        Volume: volume,
        VolumeSecond: volumeSecond
      })
    }
  })
  return orders
}

export const getActionPlanDeliveryYears = (contract: FullContract, isAdditionalOrder: boolean): number[] => {
  const years = []
  const startYear = isAdditionalOrder ? (new Date()).getFullYear() : contract.Year
  for (let i = startYear; i < (startYear + 10); i++) {
    years.push(i)
  }
  return years
}

export const getActionPlanReallocatedAreaLabel = (actionPlan: ActionPlan): string => {
  const area = reallocatedAreas.value && reallocatedAreas.value.find(r => r.Id === actionPlan.ReallocatedArea)
  return area && area.Name ? area.Name : ''
}

export const getTypeFromActionPlan = (actionPlan: ActionPlan): ActionPlanType => {
  return ACTIONPLAN_TYPES.find(type => actionPlan[type.Field])
}

export const getActionPlansByType = (actionPlans: ActionPlan[]): {[key: string]: ActionPlan[]} => {
  const groupedList = {}
  ACTIONPLAN_TYPES.forEach((type) => {
    groupedList[type.Name] = actionPlans ? actionPlans.filter((actionPlan: ActionPlan) => actionPlan[type.Field] && !actionPlan.Deleted) : []
  })
  return groupedList
}

const getActionPlanByName = (name: string): ActionPlanType => ACTIONPLAN_TYPES.find(a => a.Name === name)

export const showActionPlanNumberOfPlants = (name: string): boolean => {
  return [ACTIONPLAN_TYPES_PLANTING, ACTIONPLAN_TYPES_PLANTSALE, ACTIONPLAN_TYPES_ADDITIVE_PLANTING].includes(getActionPlanByName(name)?.Id)
}

export const showActionPlanNumberOfMeters = (name: string): boolean => {
  return getActionPlanByName(name)?.Id === ACTIONPLAN_TYPES_DITCH_CLEARING
}

export const showFixedPriceSelector = (name: string, contractType: number): boolean => {
  return contractType === CONTRACT_TYPE_SILVICULTURE_AGREEMENT && [ACTIONPLAN_TYPES_SCARIFICATION,
    ACTIONPLAN_TYPES_SCARIFICATION_WITH_EXCAVATOR,
    ACTIONPLAN_TYPES_PLANTING,
    ACTIONPLAN_TYPES_PLANTSALE,
    ACTIONPLAN_TYPES_DITCH_CLEARING,
    ACTIONPLAN_TYPES_SPRAYING,
    ACTIONPLAN_TYPES_PRECLEARING_THINNING,
    ACTIONPLAN_TYPES_PRECLEARING_FELLING,
    ACTIONPLAN_TYPES_ADDITIVE_PLANTING,
    ACTIONPLAN_TYPES_FERTILIZATION,
    ACTIONPLAN_TYPES_YOUNG_TREE_CARE,
    ACTIONPLAN_TYPES_STEMPRUNING,
    ACTIONPLAN_TYPES_PLANTATION_CONTROL,
    ACTIONPLAN_TYPES_SCARIFICATION_WITH_SOWING].includes(getActionPlanByName(name)?.Id)
}

export const getActionPlanDeliveryFieldByName = (_actionPlan: ActionPlan, name: string): string => {
  return getActionPlanByName(name)?.DateField || null
}

export const getActionTypeAllowedMonthsByName = (name: string): number[] => {
  return getActionPlanByName(name)?.allowedMonths || []
}

export const getActionPlanDeliveryDateByName = (actionPlan: ActionPlan, name: string): string => {
  const dateField = getActionPlanDeliveryFieldByName(actionPlan, name)
  return dateField ? actionPlan[dateField] : ''
}
